import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { AlertTriangle, GamepadIcon, ShieldAlert, Zap } from 'lucide-react'
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/common/ui/alert-dialog'
import { Button } from '@/common/ui/button'
import { cn } from '@/common/lib/utils'
import { formatNumber } from '@/common/lib/utils'
import { useQueryGetCurrentQuota } from '../../api/hooks/useQueryGetCurrentQuota'
import { PurchaseQuotaDialog } from '../PurchaseQuotaDialog'

const WARNING_THRESHOLD_PERCENT = 15
const URGENT_THRESHOLD_PERCENT = 10
const CRITICAL_THRESHOLD_PERCENT = 5

const WARNING_DISMISSED_STORAGE_KEY = 'low-quota-warning-dismissed'
const URGENT_SNOOZE_STORAGE_KEY = 'low-quota-urgent-snoozed-until'
const URGENT_SNOOZE_DURATION_MS = 30_000 // 30 seconds for urgent

type Severity = 'warning' | 'urgent' | 'critical'

type QuotaInfo = {
	label: string
	remaining: number
	total: number
	percentage: number
}

function getSeverity(percentage: number): Severity {
	if (percentage < CRITICAL_THRESHOLD_PERCENT) return 'critical'
	if (percentage < URGENT_THRESHOLD_PERCENT) return 'urgent'
	return 'warning'
}

const SEVERITY_CONFIG: Record<
	Severity,
	{
		title: string
		accent: string
		border: string
		bg: string
		barFill: string
		iconBg: string
		iconColor: string
		icon: typeof AlertTriangle
		buttonVariant: 'destructive' | 'default'
	}
> = {
	warning: {
		title: 'โควต้าเหลือน้อย',
		accent: 'text-amber-600 dark:text-amber-500',
		border: 'border-amber-400/60',
		bg: 'bg-amber-50 dark:bg-amber-950/30',
		barFill: 'bg-amber-500',
		iconBg: 'bg-amber-100 dark:bg-amber-900/40',
		iconColor: 'text-amber-600 dark:text-amber-400',
		icon: AlertTriangle,
		buttonVariant: 'default',
	},
	urgent: {
		title: 'โควต้าเหลือน้อยมาก',
		accent: 'text-orange-600 dark:text-orange-500',
		border: 'border-orange-500/70',
		bg: 'bg-orange-50 dark:bg-orange-950/30',
		barFill: 'bg-orange-500',
		iconBg: 'bg-orange-100 dark:bg-orange-900/40',
		iconColor: 'text-orange-600 dark:text-orange-400',
		icon: Zap,
		buttonVariant: 'destructive',
	},
	critical: {
		title: 'โควต้าใกล้หมด! ระบบอาจหยุดให้บริการ',
		accent: 'text-destructive',
		border: 'border-destructive',
		bg: 'bg-destructive/10',
		barFill: 'bg-destructive',
		iconBg: 'bg-destructive/20',
		iconColor: 'text-destructive',
		icon: ShieldAlert,
		buttonVariant: 'destructive',
	},
}

const PROJECT_NAME = import.meta.env.VITE_PROJECT_NAME || ''
const IS_DEMO_PROJECT = PROJECT_NAME.includes('a-tee8')

export function LowQuotaWarningDialog() {
	const { data } = useQueryGetCurrentQuota()
	const location = useLocation()
	const [open, setOpen] = useState(false)
	const [purchaseOpen, setPurchaseOpen] = useState(false)

	const lowQuotas = useMemo<QuotaInfo[]>(() => {
		if (!data) return []

		const items: QuotaInfo[] = []

		const winningTotal = data.winningQuota ?? 0
		const winningRemaining = winningTotal - (data.winningQuotaUsed ?? 0)
		if (winningTotal > 0) {
			const percentage = (winningRemaining / winningTotal) * 100
			if (percentage < WARNING_THRESHOLD_PERCENT) {
				items.push({
					label: 'โควตายอดได้เสีย (Winning Quota)',
					remaining: winningRemaining,
					total: winningTotal,
					percentage,
				})
			}
		}

		const creditTotal = data.creditQuota ?? 0
		const creditRemaining = creditTotal - (data.creditQuotaUsed ?? 0)
		if (creditTotal > 0) {
			const percentage = (creditRemaining / creditTotal) * 100
			if (percentage < WARNING_THRESHOLD_PERCENT) {
				items.push({
					label: 'โควตายอดการเงิน (Credit Quota)',
					remaining: creditRemaining,
					total: creditTotal,
					percentage,
				})
			}
		}

		return items
	}, [data])

	const { worstPercentage, severity } = useMemo(() => {
		if (lowQuotas.length === 0) {
			return {
				worstPercentage: null as number | null,
				severity: null as Severity | null,
			}
		}
		const worst = lowQuotas.reduce(
			(min, q) => (q.percentage < min ? q.percentage : min),
			Number.POSITIVE_INFINITY,
		)
		return {
			worstPercentage: worst,
			severity: getSeverity(worst),
		}
	}, [lowQuotas])

	useEffect(() => {
		if (lowQuotas.length === 0 || severity === null) {
			setOpen(false)
			return
		}

		// Critical (< 5%): always show immediately, no snooze option
		if (severity === 'critical') {
			setOpen(true)
			return
		}

		// Urgent (< 10%): check snooze, if expired then show again.
		if (severity === 'urgent') {
			const snoozedUntil = Number(
				sessionStorage.getItem(URGENT_SNOOZE_STORAGE_KEY) ?? '0',
			)
			if (Date.now() >= snoozedUntil) {
				setOpen(true)
			}
			return
		}

		// Warning (< 15%): once dismissed, stay dismissed for the whole session.
		const dismissed =
			sessionStorage.getItem(WARNING_DISMISSED_STORAGE_KEY) === '1'
		if (!dismissed) {
			setOpen(true)
		}
	}, [lowQuotas.length, severity, location.pathname])

	// Re-check periodically for urgent/critical snooze expiry while staying on the page.
	useEffect(() => {
		if (severity === 'urgent') {
			const interval = window.setInterval(() => {
				const snoozedUntil = Number(
					sessionStorage.getItem(URGENT_SNOOZE_STORAGE_KEY) ?? '0',
				)
				if (Date.now() >= snoozedUntil) setOpen(true)
			}, 10_000) // Check every 10 seconds
			return () => window.clearInterval(interval)
		}
		// Critical: no auto-reopen on same page, only on navigation/refresh
	}, [severity])

	const persistDismissal = () => {
		// Critical: DO NOT persist — will re-open on every route change/refresh
		if (severity === 'critical') return

		// Urgent: 30-second snooze so it re-opens automatically even on the same page
		if (severity === 'urgent') {
			sessionStorage.setItem(
				URGENT_SNOOZE_STORAGE_KEY,
				String(Date.now() + URGENT_SNOOZE_DURATION_MS),
			)
			return
		}
		// Warning: stay dismissed for the whole session (no time-based re-open).
		sessionStorage.setItem(WARNING_DISMISSED_STORAGE_KEY, '1')
	}

	const handleDismiss = () => {
		persistDismissal()
		setOpen(false)
	}

	const handlePurchase = () => {
		persistDismissal()
		setOpen(false)
		setPurchaseOpen(true)
	}

	if (IS_DEMO_PROJECT) {
		return null
	}

	if (lowQuotas.length === 0 || !severity || worstPercentage === null) {
		return null
	}

	const config = SEVERITY_CONFIG[severity]
	const Icon = config.icon
	const isCritical = severity === 'critical'

	return (
		<>
			<AlertDialog open={open}>
				<AlertDialogContent
					className={cn('max-w-md border-2', config.border)}
					onEscapeKeyDown={(e) => e.preventDefault()}
				>
					<AlertDialogHeader>
						<div className="flex items-start gap-3">
							<div
								className={cn(
									'flex h-12 w-12 shrink-0 items-center justify-center rounded-full',
									config.iconBg,
									isCritical && 'animate-pulse',
								)}
							>
								<Icon className={cn('h-6 w-6', config.iconColor)} />
							</div>
							<div className="flex-1 space-y-1">
								<AlertDialogTitle className={config.accent}>
									{config.title}
								</AlertDialogTitle>
								<AlertDialogDescription>
									โควต้าคงเหลือต่ำกว่า {WARNING_THRESHOLD_PERCENT}%
									กรุณาซื้อโควต้าเพิ่มทันที
								</AlertDialogDescription>
							</div>
						</div>
					</AlertDialogHeader>

					{/* Impact banner */}
					<div
						className={cn(
							'flex items-start gap-2 rounded-md border p-3',
							config.border,
							config.bg,
						)}
					>
						<GamepadIcon
							className={cn('mt-0.5 h-4 w-4 shrink-0', config.iconColor)}
						/>
						<div className="space-y-0.5 text-sm">
							<p className={cn('font-semibold', config.accent)}>
								ผลกระทบต่อผู้เล่น
							</p>
							<p className="text-muted-foreground">
								หากโควต้าหมด{' '}
								<span className="font-semibold text-foreground">
									ผู้เล่นจะไม่สามารถเปิดเกมได้
								</span>{' '}
								และระบบจะหยุดให้บริการจนกว่าจะเติมโควต้า
							</p>
						</div>
					</div>

					{/* Quota details */}
					<div className="space-y-2">
						{lowQuotas.map((quota) => {
							const itemSeverity = getSeverity(quota.percentage)
							const itemConfig = SEVERITY_CONFIG[itemSeverity]
							return (
								<div
									key={quota.label}
									className={cn(
										'space-y-2 rounded-lg border p-3',
										itemConfig.border,
										itemConfig.bg,
									)}
								>
									<div className="flex items-center justify-between">
										<span className="text-sm font-medium">{quota.label}</span>
										<span
											className={cn('text-sm font-bold', itemConfig.accent)}
										>
											{quota.percentage.toFixed(2)}%
										</span>
									</div>
									<div className="h-2 overflow-hidden rounded-full bg-muted">
										<div
											className={cn(
												'h-full transition-all',
												itemConfig.barFill,
											)}
											style={{
												width: `${Math.max(quota.percentage, 2).toFixed(2)}%`,
											}}
										/>
									</div>
									<div className="flex items-center justify-between text-xs text-muted-foreground">
										<span>คงเหลือ</span>
										<span>
											{formatNumber(quota.remaining)} /{' '}
											{formatNumber(quota.total)}
										</span>
									</div>
								</div>
							)
						})}
					</div>

					<AlertDialogFooter className="gap-2">
						<Button variant="outline" onClick={handleDismiss}>
							{isCritical
								? 'ปิด (จะเตือนอีกครั้งเมื่อเปลี่ยนหน้า)'
								: severity === 'urgent'
									? 'ปิด (จะเตือนอีกครั้งใน 30 วินาที)'
									: 'ปิด'}
						</Button>
						<Button
							variant={config.buttonVariant}
							onClick={handlePurchase}
							className={cn(isCritical && 'animate-pulse')}
						>
							ซื้อโควต้าเพิ่มทันที
						</Button>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<PurchaseQuotaDialog open={purchaseOpen} onOpenChange={setPurchaseOpen} />
		</>
	)
}
